import { useMemo, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'

import {
  generateOTP,
  enable2FA,
  POST_GENERATE_OTP_KEY,
  POST_ENABLE_2FA_KEY,
} from '@/actions/authActions'
import { selectErrorsForKeys } from '@/reducers/fetch'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Alert, Button, Dialog, Form, Passcode, Text } from '@/ui'
import { ImageBackgroundColors, TextColors, TypographyStyles } from '@/styles'
import { getFieldNames, makeReqPhoneNumberSchema } from '@/base'

interface EnableDialogContentProps {
  onSuccess: () => void
}

const EnableDialogContent = ({ onSuccess }: EnableDialogContentProps) => {
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  const [screen, setScreen] = useState('start')
  const [otp, setOtp] = useState('')
  const [resentCode, setResentCode] = useState(false)

  const errors = useReselector(selectErrorsForKeys, [
    POST_GENERATE_OTP_KEY,
    POST_ENABLE_2FA_KEY,
  ])

  const generatePasscode = async () => {
    if (!phoneNumber) {
      return
    }
    const result = await generateOTP({ phoneNumber })(dispatch)

    if (result) {
      setScreen('code')
    }
  }

  const formik = useFormik({
    initialValues: {
      phoneNumber: user?.phoneNumber,
    },
    onSubmit: generatePasscode,
  })

  const {
    submitForm,
    isValid,
    values: { phoneNumber },
  } = formik
  const fieldNames = getFieldNames(formik)

  const validateAndEnable = async () => {
    if (!phoneNumber) {
      return
    }
    const result = await enable2FA({
      passcode: otp,
      phoneNumber,
    })(dispatch)

    if (result) {
      onSuccess()
    }

    setResentCode(false)
  }

  const errorView = useMemo(
    () =>
      errors.length > 0 ? (
        <Alert
          variant="error"
          title="It looks like this passcode could not be verified. Please double check your passcode, or generate a new one."
        />
      ) : null,
    [errors]
  )

  if (screen === 'code') {
    return (
      <>
        <Dialog.Title>Secure your account with 2FA</Dialog.Title>

        <Dialog.Description>
          We&#39;ve sent a 6 digit verification code to your number ending in:{' '}
          {phoneNumber?.slice(-4)}. This may take a couple of minutes to arrive.
        </Dialog.Description>

        {errorView}

        <Passcode value={otp} handleChange={setOtp} />

        <Button
          variant="link"
          onClick={() => {
            generatePasscode()
            setOtp('')
            setResentCode(true)
          }}
        >
          Didn&#39;t receive a text? Resend code.
        </Button>

        {resentCode && <Text className="italic">Code resent.</Text>}

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary" onClick={() => setScreen('start')}>
              Close
            </Button>
          </Dialog.Close>

          <Button disabled={!otp} onClick={validateAndEnable}>
            Continue
          </Button>
        </Dialog.Footer>
      </>
    )
  }

  return (
    <FormikProvider value={formik}>
      <Dialog.Title>Secure your account with 2FA</Dialog.Title>
      <Dialog.Description className="my-4">
        Two-factor authentication is an extra layer of security. Once enabled,
        you&#39;ll be required to give two types of identification when you log
        into Heard. We currently only allow 2FA via text message.
      </Dialog.Description>

      {errorView}

      <Form.Input
        label="Enter your phone number:"
        name={fieldNames.phoneNumber}
        placeholder="(000) 000-0000"
        inputClassName="mt-2"
        componentType="phone"
        schema={makeReqPhoneNumberSchema()}
      />

      <Text
        variant={TypographyStyles.BODY_SM}
        className="mt-2"
        color={TextColors.MUTED}
      >
        <i>
          You&#39;ll receive a 6-digit verification by text message. Text or
          data rates may apply.
        </i>
      </Text>
      <br />
      <Dialog.Footer>
        <Dialog.Close asChild>
          <Button variant="secondary">Close</Button>
        </Dialog.Close>

        <Button disabled={!isValid} onClick={submitForm}>
          Continue
        </Button>
      </Dialog.Footer>
    </FormikProvider>
  )
}

export const EnableDialog = () => {
  const [open, setOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <Dialog.Trigger asChild>
          <Button className="w-full md:w-fit" onClick={() => setOpen(true)}>
            Set up
          </Button>
        </Dialog.Trigger>

        <Dialog.Content onDismiss={() => setOpen(false)}>
          <EnableDialogContent
            onSuccess={() => {
              setOpen(false)
              setShowSuccess(true)
            }}
          />
        </Dialog.Content>
      </Dialog>

      <Dialog open={showSuccess}>
        <Dialog.Content
          heading="Phone number successfully verified"
          illustrationSrc="https://heard-images.s3.amazonaws.com/assets/check.svg"
          illustrationAlt="Success"
          illustrationColor={ImageBackgroundColors.GREEN}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Dialog.Footer>
            <Button onClick={() => setShowSuccess(false)}>Dismiss</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}
